import { ENTITY_PREFIX } from '../../../../../constants';
import { formatEntityId } from '../../../../../helpers';
import { buildTpOptionsRows, derivePlanFlags, toClientOption } from '../utils';
import useFetchTreatmentPlanV3Header from './useFetchTreatmentPlanV3Header';
import useHeaderDialog from './useHeaderDialog';
import useNoActivePlanAlert from './useNoActivePlanAlert';
import useTreatmentPlanV3ClientSelect from './useTreatmentPlanV3ClientSelect';
import useTreatmentPlanV3HeaderState from './useTreatmentPlanV3HeaderState';
import useTreatmentPlanV3PlanActions from './useTreatmentPlanV3PlanActions';
import useTreatmentPlanV3ProgressReport from './useTreatmentPlanV3ProgressReport';
import {
  buildHeaderConfirmHandlers,
  buildHeaderMenuHandlers,
  buildPdfDocumentProps,
} from './utils';

import type { UseTreatmentPlanV3HeaderResult } from '../components/types';

export default function useTreatmentPlanV3Header(): UseTreatmentPlanV3HeaderResult {
  useFetchTreatmentPlanV3Header();
  const state = useTreatmentPlanV3HeaderState();
  const dialog = useHeaderDialog();
  useNoActivePlanAlert(dialog, state);
  const clientSelect = useTreatmentPlanV3ClientSelect(dialog);
  const plan = useTreatmentPlanV3PlanActions(dialog);
  const report = useTreatmentPlanV3ProgressReport(dialog);

  const {
    user,
    provider,
    selectedClient,
    isClientsLoading,
    treatmentPlan,
    treatmentPlans,
    activeTreatmentPlan,
    tpStatusIdByLabel,
    isPlansLoading,
    isReportLoading,
  } = state;

  const flags = derivePlanFlags(treatmentPlans, tpStatusIdByLabel);
  const entries = buildTpOptionsRows({
    userRoles: user?.roles ?? [],
    userEmail: user?.email,
    providerEmail: provider?.email,
    treatmentPlan,
    treatmentPlans,
    activeTreatmentPlan,
    tpStatusIdByLabel,
    flags,
    isReportRunning: report.isReportRunning,
  });
  const menuHandlers = buildHeaderMenuHandlers(plan, report);
  const confirmHandlers = buildHeaderConfirmHandlers(dialog, clientSelect, plan, report);

  const onConfirm = () => {
    if (!dialog.activeDialog) return;
    void confirmHandlers[dialog.activeDialog.kind]();
  };

  return {
    clientSelect: {
      value: clientSelect.value,
      options: clientSelect.options,
      disabled: isClientsLoading || isPlansLoading || isReportLoading,
      onChange: clientSelect.handleChange,
    },
    menu: {
      entries,
      disabled: !selectedClient,
      treatmentPlan,
      treatmentPlans,
      activeTreatmentPlan,
      tpStatusIdByLabel,
      inProgressTpId: flags.inProgressTpId,
      onItemClick: menuHandlers.onItemClick,
      onBayPick: menuHandlers.onBayPick,
      onPlanPick: plan.requestSwitchPlan,
    },
    dialogs: {
      activeDialog: dialog.activeDialog,
      onClose: dialog.closeDialog,
      onConfirm,
      isSubmitting: plan.isSubmitting,
      isReportLoading,
      planLabel: formatEntityId(treatmentPlan?.id ?? '', ENTITY_PREFIX.TreatmentPlan),
      activePlanLabel: formatEntityId(activeTreatmentPlan?.id ?? '', ENTITY_PREFIX.TreatmentPlan),
      clientLabel: selectedClient ? toClientOption(selectedClient).label : '',
      isPlanActive: Boolean(treatmentPlan?.is_active),
    },
    report: {
      contentRef: report.contentRef,
      isReportReady: report.isReportReady,
      pdfProps: buildPdfDocumentProps(state),
    },
  };
}
